<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * A number field that takes numbers only: digits, one decimal separator (a comma or a dot,
 * shown as the interface language writes it) and a minus where the value may be negative.
 * Anything else typed or pasted is dropped as it comes. `update:modelValue` follows the typing
 * (a number or null); `change` fires once the field is left or Enter is pressed, for callers
 * that save then.
 */
const props = defineProps({
  modelValue: { type: [Number, String, null], default: null },
  integer: Boolean,
  min: { type: Number, default: null },
  max: { type: Number, default: null },
  disabled: Boolean,
  invalid: Boolean,
  id: { type: String, default: '' },
  placeholder: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'change'])
const { locale } = useI18n()

const separator = computed(() => (new Intl.NumberFormat(locale.value).formatToParts(1.5).find((p) => p.type === 'decimal')?.value === ',' ? ',' : '.'))
const negative = computed(() => props.min === null || props.min < 0)

const show = (v) => (v === null || v === undefined || v === '' ? '' : String(v).replace('.', separator.value))
/** A number, null when empty, undefined while it is not one yet ("-"). */
function parse(text) {
  if (text === '' || text === '-') return text === '' ? null : undefined
  const n = Number(text.replace(',', '.'))
  return Number.isNaN(n) ? undefined : n
}

const text = ref(show(props.modelValue))
watch(() => props.modelValue, (v) => {
  // Outside changes (a reset, a loaded record) replace the text; the person's own typing stays as typed.
  if ((parse(text.value) ?? null) !== (v === '' || v === undefined ? null : Number(v))) text.value = show(v)
})

/** What may stay of the typed text, and where the caret goes. */
function clean(raw, caret) {
  let out = ''
  let pos = caret
  let decimal = false
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]
    const keep = /\d/.test(ch)
      || (ch === '-' && negative.value && out === '')
      || ((ch === ',' || ch === '.') && !props.integer && !decimal)
    if (keep) {
      if (ch === ',' || ch === '.') decimal = true
      out += ch === ',' || ch === '.' ? separator.value : ch
    } else if (i < caret) {
      pos--
    }
  }
  return { out, pos }
}

function onInput(e) {
  const el = e.target
  const { out, pos } = clean(el.value, el.selectionStart ?? el.value.length)
  if (out !== el.value) {
    el.value = out
    el.setSelectionRange?.(pos, pos)
  }
  text.value = out
  const n = parse(out)
  if (n !== undefined) emit('update:modelValue', n)
}
function commit() {
  let n = parse(text.value)
  if (n === undefined) n = null
  if (n !== null && props.min !== null && n < props.min) n = props.min
  if (n !== null && props.max !== null && n > props.max) n = props.max
  text.value = show(n)
  emit('update:modelValue', n)
  emit('change', n)
}
</script>

<template>
  <input
    :id="id || undefined"
    type="text"
    :inputmode="integer ? (negative ? 'text' : 'numeric') : 'decimal'"
    autocomplete="off"
    :class="['input text-right tabular-nums', invalid ? 'invalid' : '']"
    :value="text"
    :disabled="disabled"
    :placeholder="placeholder"
    @input="onInput"
    @change="commit"
  />
</template>
